import { useContext } from 'react';
import { ServicesContext } from 'app/items/shared/context_interface';
import { createAxiosInstance } from 'app/agriculture/queries/createAxiosInstance';
import { useMutation } from '@tanstack/react-query';
import { useSelectedFarm } from 'app/agriculture/hooks/useSelectedFarm/useSelectedFarm';
import {
    entityConfig,
    entityConfigToType,
    MutableEntityType,
} from 'app/agriculture/mutations/types/entityConfig';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';
import { useEntityCacheInvalidation } from 'app/agriculture/mutations/useEntityCacheInvalidation';
import { convertDatePropertiesToTZDate } from 'app/agriculture/queries/utils/convertDatePropertiesToTZDate';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';

/**
 * This mutation does not support entities with snapshots
 */
export function useEntityCreateMutation<T extends MutableEntityType>(entityType: T) {
    const { timeZone } = useTimeZone();
    const { agricultureToastService } = useContext(ServicesContext);

    const instance = createAxiosInstance();
    const farm = useSelectedFarm();
    const config = entityConfig[entityType];

    const { addToCache } = useEntityCacheInvalidation(entityType);

    const { mutate, isPending } = useMutation({
        mutationFn: async function (payload: Partial<entityConfigToType[MutableEntityType]>) {
            const response = await instance.post(config.endpoint, { ...payload, farmId: farm.id });
            return config.schema
                .transform((data) => convertDatePropertiesToTZDate(data, timeZone))
                .parse(response.data) as entityConfigToType[T];
        },
        onSuccess: function (data: entityConfigToType[T]) {
            addToCache(data);
        },
        onError: async function (error) {
            console.error('Error creating entity', error);
            await agricultureToastService.showToast({
                title: `${entityType} Creation Failed`,
                subtext: 'Something went wrong',
                type: AgricultureToastType.ERROR,
                variant: 'simple',
            });
        },
    });
    return { mutate, isPending };
}
